import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { Button } from '../../../components/ui/Button';
import { TextField } from '../../../components/ui/Field';
import { ErrorState } from '../../../components/ui/Page';
import { ApiError } from '../../../services/http';
import { readPreference } from '../../../utils/storage';
import { useAuth } from '../AuthProvider';
import { AuthLayout } from './AuthLayout';

const schema = z.object({
  workspace: z.string().trim().toLowerCase().min(1, 'Enter your workspace'),
  email: z.string().trim().email('Enter a valid email'),
  password: z.string().min(1, 'Enter your password'),
});
type Values = z.infer<typeof schema>;

export function homeFor(type: 'tenant' | 'platform' | undefined): string {
  return type === 'platform' ? '/platform' : '/app';
}

export function LoginPage() {
  const { status, me, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<unknown>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { workspace: readPreference('workspace') ?? '', email: '', password: '' },
  });

  if (status === 'authenticated') return <Navigate to={homeFor(me?.user.type)} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await login(values);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from.startsWith('/app') ? from : '/app', { replace: true });
    } catch (err) {
      setError(err);
      form.resetField('password');
    }
  });

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use your business workspace and work email."
      footer={
        <span className="flex flex-col items-center gap-1">
          <Link to="/signup" className="hover:text-fg">
            New business? Start a free trial
          </Link>
          <Link to="/platform/login" className="hover:text-fg">
            Platform administrator sign-in
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error ? <ErrorState error={error} /> : null}
        <TextField
          label="Workspace"
          hint="The short name of your business account, e.g. acme"
          autoComplete="organization"
          autoCapitalize="none"
          error={form.formState.errors.workspace?.message}
          {...form.register('workspace')}
        />
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        <div className="flex items-center justify-between">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand-700 hover:underline dark:text-brand-300"
          >
            Forgot password?
          </Link>
          <Button type="submit" loading={form.formState.isSubmitting}>
            Sign in
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

const platformSchema = schema.omit({ workspace: true }).extend({ totpCode: z.string().trim().optional() });

export function PlatformLoginPage() {
  const { status, me, platformLogin } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  // Phase 11: admins with two-factor sign-in are asked for their authenticator code after the password.
  const [needsCode, setNeedsCode] = useState(false);
  const form = useForm<z.infer<typeof platformSchema>>({
    resolver: zodResolver(platformSchema),
    defaultValues: { email: '', password: '', totpCode: '' },
  });

  if (status === 'authenticated') return <Navigate to={homeFor(me?.user.type)} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await platformLogin({
        email: values.email,
        password: values.password,
        ...(needsCode && values.totpCode ? { totpCode: values.totpCode } : {}),
      });
      navigate('/platform', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MFA_REQUIRED') {
        // Keep the password: only the code is missing or wrong.
        if (needsCode) setError(err);
        setNeedsCode(true);
        form.resetField('totpCode');
        return;
      }
      setError(err);
      form.resetField('password');
    }
  });

  return (
    <AuthLayout
      title="Platform console"
      subtitle="Sign in as a platform administrator."
      footer={<Link to="/login">Business sign-in</Link>}
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error ? <ErrorState error={error} /> : null}
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />
        {needsCode ? (
          <TextField
            label="Authenticator code"
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
            hint="The 6-digit code from your authenticator app, or one of your recovery codes."
            {...form.register('totpCode')}
          />
        ) : null}
        <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
          {needsCode ? 'Verify and sign in' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}
